import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { domainAPI, auctionAPI, paymentAPI, watchlistAPI, stripeAPI } from '../lib/api';
import { ArrowLeft, Clock, TrendingUp, Globe, Calendar, BarChart3, Shield, Loader2, Heart, CreditCard, ShoppingCart, DollarSign, Wifi } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

function formatTimeLeft(endStr) {
  if (!endStr) return '';
  const end = new Date(endStr);
  const now = new Date();
  const diff = end - now;
  if (diff <= 0) return 'Auction ended';
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${d}d ${h}h ${m}m`;
}

export default function DomainDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [domain, setDomain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState('');
  const [bidding, setBidding] = useState(false);
  const [isWatchlisted, setIsWatchlisted] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [sseConnected, setSseConnected] = useState(false);
  const eventSourceRef = useRef(null);

  useEffect(() => {
    domainAPI.get(id).then(r => { setDomain(r.data); setLoading(false); }).catch(() => { setLoading(false); });
    if (user) {
      watchlistAPI.getIds().then(r => { if (r.data.ids?.includes(id)) setIsWatchlisted(true); }).catch(() => {});
    }
  }, [id, user]);

  // SSE: Real-time bid updates
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const es = new EventSource(`${apiUrl}/api/auctions/${id}/stream`);
    eventSourceRef.current = es;
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'connected') setSseConnected(true);
        if (data.type === 'new_bid') {
          setDomain(prev => prev ? { ...prev, current_bid: data.amount, bid_count: data.bid_count,
            bids: [{ id: data.bid_id, amount: data.amount, user_name: data.user_name, created_at: data.timestamp }, ...(prev.bids || []).slice(0, 9)]
          } : prev);
          toast.info(`New bid: $${data.amount.toLocaleString()} by ${data.user_name}`);
        }
      } catch {}
    };
    es.onerror = () => setSseConnected(false);
    return () => { es.close(); };
  }, [id]);

  const toggleWatchlist = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      if (isWatchlisted) { await watchlistAPI.remove(id); setIsWatchlisted(false); toast.success('Removed from watchlist'); }
      else { await watchlistAPI.add(id); setIsWatchlisted(true); toast.success('Added to watchlist'); }
    } catch { toast.error('Failed'); }
  };

  const handlePurchase = async () => {
    if (!user) { navigate('/login'); return; }
    setPurchasing(true);
    try {
      const res = await paymentAPI.createOrder({ domain_id: id, payment_method: 'mypos' });
      setShowPayment(true);
      toast.success('Order created! Proceed with payment.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    }
    setPurchasing(false);
  };

  const handleBid = async () => {
    if (!user) { navigate('/login'); return; }
    const amount = parseFloat(bidAmount);
    if (!amount || amount <= 0) { toast.error('Enter a valid bid amount'); return; }
    setBidding(true);
    try {
      await auctionAPI.bid(id, { amount });
      toast.success('Bid placed successfully!');
      const res = await domainAPI.get(id);
      setDomain(res.data);
      setBidAmount('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to place bid');
    }
    setBidding(false);
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;
  if (!domain) return <div className="text-center py-32 text-zinc-500">Domain not found</div>;

  const isAuction = domain.is_auction;
  const timeLeft = isAuction ? formatTimeLeft(domain.auction_end) : null;

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="domain-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-[#00E5FF] mb-8 transition-colors" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> {t('common.back')}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="border-zinc-700 text-zinc-500 text-xs uppercase">{domain.category}</Badge>
                {isAuction && <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">Auction</Badge>}
                {domain.is_featured && <Badge className="bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/20">Featured</Badge>}
              </div>
              <button onClick={toggleWatchlist}
                className={`p-2 rounded-sm transition-colors ${isWatchlisted ? 'text-[#FF6B9D]' : 'text-zinc-600 hover:text-[#FF6B9D]'}`} data-testid="detail-watchlist-btn">
                <Heart className={`h-5 w-5 ${isWatchlisted ? 'fill-current' : ''}`} />
              </button>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white font-['Outfit'] tracking-tight" data-testid="domain-name">
              {domain.name}<span className="text-[#00E5FF]">{domain.tld}</span>
            </h1>
            {domain.description && (
              <p className="mt-4 text-zinc-400 leading-relaxed">{domain.description}</p>
            )}
          </div>

          {/* Domain Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Globe, label: 'Extension', value: domain.tld },
              { icon: Calendar, label: 'Domain Age', value: `${domain.age_years || 0} years` },
              { icon: BarChart3, label: 'Monthly Traffic', value: `${(domain.traffic_monthly || 0).toLocaleString()}` },
              { icon: Shield, label: 'Registrar', value: domain.registrar || 'N/A' },
            ].map((stat, i) => (
              <div key={i} className="p-4 bg-[#0A0A0A] border border-white/5 rounded-sm">
                <stat.icon className="h-5 w-5 text-[#00E5FF] mb-2" />
                <div className="text-[10px] text-zinc-600 uppercase tracking-wider">{stat.label}</div>
                <div className="text-sm font-semibold text-white mt-0.5">{stat.value}</div>
              </div>
            ))}
          </div>

          {/* Bid History */}
          {isAuction && domain.bids?.length > 0 && (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">{t('auction.bidHistory')}</h3>
              <div className="space-y-2">
                {domain.bids.map((bid, i) => (
                  <div key={bid.id || i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <div>
                      <span className="text-sm text-white">{bid.user_name}</span>
                      <span className="text-xs text-zinc-600 ml-2">{new Date(bid.created_at).toLocaleString()}</span>
                    </div>
                    <span className="text-sm font-bold text-[#00E5FF]">${bid.amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - Purchase/Bid */}
        <div className="space-y-4">
          <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6 sticky top-24">
            {/* Real-time indicator */}
            {isAuction && (
              <div className={`flex items-center gap-2 mb-3 text-xs ${sseConnected ? 'text-green-400' : 'text-zinc-600'}`} data-testid="sse-status">
                <Wifi className="h-3 w-3" />
                <span>{sseConnected ? 'Live updates connected' : 'Connecting...'}</span>
              </div>
            )}
            {isAuction ? (
              <>
                {timeLeft && (
                  <div className="flex items-center gap-2 mb-4 text-amber-400">
                    <Clock className="h-4 w-4" />
                    <span className="text-sm font-medium">{timeLeft}</span>
                  </div>
                )}
                <div className="mb-4">
                  <span className="text-xs text-zinc-600 uppercase tracking-wider">{t('domains.currentBid')}</span>
                  <div className="text-3xl font-bold text-white font-['Outfit'] flex items-center gap-2 mt-1">
                    <TrendingUp className="h-5 w-5 text-[#00E5FF]" />
                    ${(domain.current_bid || domain.min_bid || 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-zinc-600 mt-1">{domain.bid_count || 0} {t('auction.bids')}</div>
                </div>
                <div className="mb-4">
                  <span className="text-xs text-zinc-600 uppercase tracking-wider mb-1.5 block">{t('auction.yourBid')}</span>
                  <Input type="number" value={bidAmount} onChange={(e) => setBidAmount(e.target.value)}
                    placeholder={`Min $${((domain.current_bid || domain.min_bid || 0) + 1).toLocaleString()}`}
                    className="h-12 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm text-lg" data-testid="bid-amount-input" />
                </div>
                <Button onClick={handleBid} disabled={bidding}
                  className="w-full h-12 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="place-bid-btn">
                  {bidding ? <Loader2 className="h-4 w-4 animate-spin" /> : t('auction.bidNow')}
                </Button>
                {/* Financing option for auctions */}
                {(domain.registrar === 'NameSilo' || domain.source === 'namesilo_import') && (
                  <Link to={`/financing/${id}`}>
                    <Button variant="outline" className="w-full mt-3 border-amber-500/30 text-amber-400 hover:border-amber-500 rounded-sm" data-testid="financing-link-btn">
                      <DollarSign className="h-4 w-4 mr-2" /> Apply for Financing
                    </Button>
                  </Link>
                )}
              </>
            ) : (
              <>
                <div className="mb-6">
                  <span className="text-xs text-zinc-600 uppercase tracking-wider">{t('common.price')}</span>
                  <div className="text-3xl font-bold text-white font-['Outfit'] mt-1">${domain.price?.toLocaleString()}</div>
                </div>
                {showPayment ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-[#00E5FF]/5 border border-[#00E5FF]/20 rounded-sm">
                      <p className="text-xs text-[#00E5FF]">Order created for {domain.name}{domain.tld}</p>
                      <p className="text-xs text-zinc-500 mt-1">Choose your payment method</p>
                    </div>
                    <Button onClick={() => { toast.success('Redirecting to myPOS checkout...'); }}
                      className="w-full h-11 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm" data-testid="proceed-mypos-btn">
                      <CreditCard className="h-4 w-4 mr-2" /> Pay with myPOS
                    </Button>
                    <Button onClick={async () => {
                      try {
                        const res = await stripeAPI.createCheckout({ domain_id: id });
                        if (res.data.checkout_url) { window.location.href = res.data.checkout_url; }
                        else { toast.info(res.data.message || 'Stripe is in demo mode. Configure in Admin > Settings.'); }
                      } catch { toast.error('Stripe checkout failed'); }
                    }} variant="outline" className="w-full h-11 border-purple-500/30 text-purple-400 hover:border-purple-500 rounded-sm font-bold uppercase tracking-wider" data-testid="proceed-stripe-btn">
                      <CreditCard className="h-4 w-4 mr-2" /> Pay with Stripe
                    </Button>
                    <Button variant="outline" onClick={() => setShowPayment(false)}
                      className="w-full border-zinc-800 text-zinc-400 rounded-sm">Cancel</Button>
                  </div>
                ) : (
                  <>
                    <Button onClick={handlePurchase} disabled={purchasing}
                      className="w-full h-12 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="buy-now-btn">
                      {purchasing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShoppingCart className="h-4 w-4 mr-2" /> {t('domains.buyNow')}</>}
                    </Button>
                    {(domain.registrar === 'NameSilo' || domain.source === 'namesilo_import') && (
                      <Link to={`/financing/${id}`}>
                        <Button variant="outline" className="w-full mt-3 border-amber-500/30 text-amber-400 hover:border-amber-500 rounded-sm" data-testid="financing-link-btn">
                          <DollarSign className="h-4 w-4 mr-2" /> Apply for Financing
                        </Button>
                      </Link>
                    )}
                  </>
                )}
              </>
            )}
            <p className="text-[10px] text-zinc-600 text-center mt-4">
              Protected by XDomainX escrow service
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
